import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceSettings } from '@/contexts/WorkspaceSettingsContext';
import { getWidgetDeepLinkTarget, getDeepLinkCompanyId, setDeepLinkCompanySwitch } from '@/lib/deep-link-utils';
import { useCompany } from '@/contexts/CompanyContext';

/**
 * Listens for `NavigationRequest` postMessages from the parent workspace
 * and navigates to the target route.
 *
 * The Access Workspace sends this message type when the user clicks a
 * deep link (e.g. from the Evo Feed) while the iframe is already loaded.
 *
 * Must be rendered inside both a Router and a WorkspaceSettingsProvider.
 */
export function WorkspaceNavigationListener(): null {
  const navigate = useNavigate();
  const { settings } = useWorkspaceSettings();
  const { currentCompany, switchCompany } = useCompany();

  useEffect(() => {
    if (!settings.workspaceUrl) return;

    let workspaceOrigin: string;
    try {
      workspaceOrigin = new URL(settings.workspaceUrl).origin;
    } catch {
      return;
    }

    function handleMessage(event: MessageEvent): void {
      if (event.origin !== workspaceOrigin) return;

      const data = parseMessageData(event.data);
      if (!data) return;

      if (
        typeof data.type === 'string' &&
        data.type.toLowerCase() === 'navigationrequest' &&
        typeof data.url === 'string'
      ) {
        const targetPath = resolveNavigationTarget(data.url);
        if (!targetPath) return;

        const companyId = getDeepLinkCompanyId(data.url);
        const needsCompanySwitch = companyId && String(currentCompany?.id) !== companyId;

        if (needsCompanySwitch) {
          setDeepLinkCompanySwitch(true);
          switchCompany(companyId).then(() => {
            navigate(stripCompanyIdParam(targetPath), { replace: true });
            setDeepLinkCompanySwitch(false);
          }).catch(() => {
            navigate(stripCompanyIdParam(targetPath), { replace: true });
            setDeepLinkCompanySwitch(false);
          });
        } else {
          navigate(stripCompanyIdParam(targetPath), { replace: true });
        }
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [settings.workspaceUrl, navigate, currentCompany, switchCompany]);

  return null;
}

/**
 * Extracts the internal app path from a workspace deep-link URL.
 * Strips the app base path prefix and deep-link-only query parameters.
 */
function extractAppPath(url: string): string | null {
  if (!url) return null;

  let pathname: string;
  let search: string;

  try {
    if (url.startsWith('/')) {
      const parts = url.split('?');
      pathname = parts[0];
      search = parts.length > 1 ? `?${parts[1]}` : '';
    } else {
      const parsed = new URL(url);
      pathname = parsed.pathname;
      search = parsed.search;
    }
  } catch {
    return null;
  }

  const cleanSearch = search
    .replace(/[?&]instanceId=[^&]*/g, '')
    .replace(/[?&]inAccessWorkspaceTab=[^&]*/g, '')
    .replace(/^&/, '?')
    .replace(/^\?&/, '?')
    .replace(/^\?$/, '');

  const result = pathname + cleanSearch;
  return result && result !== '/' ? result : null;
}

/**
 * Resolves a navigation URL to an internal app route.
 * Tries widget deep-link params first, then path-based extraction.
 */
function resolveNavigationTarget(url: string): string | null {
  const widgetTarget = getWidgetDeepLinkTarget(url);
  if (widgetTarget) return widgetTarget;

  return extractAppPath(url);
}

function parseMessageData(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'object' && raw !== null) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Not JSON — ignore
    }
  }
  return null;
}

/**
 * Strips the `companyId` query parameter from a path (e.g. `/projects/2?companyId=5` → `/projects/2`).
 * The company context is handled separately, so it should not appear in the router location.
 */
function stripCompanyIdParam(path: string): string {
  const qIndex = path.indexOf('?');
  if (qIndex < 0) return path;
  const base = path.slice(0, qIndex);
  const search = path.slice(qIndex);
  const cleaned = search
    .replace(/[?&]companyId=[^&]*/g, '')
    .replace(/^&/, '?')
    .replace(/^\?&/, '?')
    .replace(/^\?$/, '');
  return base + cleaned;
}
